import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FeatureIcon } from '#bv/components/FeatureIcons'
import { DiscordIcon } from '#bv/components/DiscordIcon'

export const Route = createFileRoute('/contact')({
  component: ContactPage,
})

function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    // Simulate form submission
    await new Promise((r) => setTimeout(r, 1200))
    setStatus('sent')
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div
        className="py-16 px-4 sm:px-6 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(0, 191, 255,0.08) 0%, transparent 100%)',
          borderBottom: '1px solid rgba(0,191,255,0.06)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-5xl sm:text-7xl tracking-wide chrome-text mb-3">CONTACT</h1>
          <p className="text-base" style={{ color: '#5a6d8a' }}>
            Fastest support is in Discord. For everything else — the form below reaches us within a few hours.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left — Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="font-display text-2xl tracking-wide chrome-text mb-6">GET IN TOUCH</h2>
              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <DiscordIcon size={18} style={{ color: '#5865F2' }} />
                    ),
                    label: 'Discord (Fastest)',
                    value: 'discord.gg/tGxhDfRkdE',
                    href: 'https://discord.gg/tGxhDfRkdE',
                    note: 'Avg. response: under 15 min',
                  },
                  {
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00bfff" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    ),
                    label: 'Email Support',
                    value: 'support@PulseDigital.store',
                    href: 'mailto:support@PulseDigital.store',
                    note: 'Avg. response: 2–6 hours',
                  },
                ].map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 rounded-xl transition-all duration-200 hover:bg-white/5"
                    style={{ border: '1px solid rgba(0,191,255,0.08)' }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,191,255,0.08)' }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: '#f0f6ff' }}>{item.label}</div>
                      <div className="text-sm mt-0.5" style={{ color: '#8ecfff' }}>{item.value}</div>
                      <div className="text-xs mt-1" style={{ color: '#5a6d8a' }}>{item.note}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Hours */}
            <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.06)' }}>
              <div className="font-semibold text-sm mb-3" style={{ color: '#a5d8ff' }}>Support Hours</div>
              <div className="space-y-2 text-sm" style={{ color: '#5a6d8a' }}>
                <div className="flex justify-between">
                  <span>Discord (Tickets)</span>
                  <span style={{ color: '#00bfff' }}>24/7</span>
                </div>
                <div className="flex justify-between">
                  <span>Email</span>
                  <span>Mon–Sun, 9am–11pm UTC</span>
                </div>
                <div className="flex justify-between">
                  <span>Live Chat</span>
                  <span>Discord only</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-3">
            <div
              className="rounded-2xl p-8"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.08)' }}
            >
              {status === 'sent' ? (
                <div className="text-center py-12">
                  <div className="flex justify-center mb-4">
                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[rgba(52,211,153,0.12)] text-[#34d399] ring-1 ring-[rgba(52,211,153,0.28)]">
                      <FeatureIcon name="check" size={40} strokeWidth={1.5} className="text-current" />
                    </span>
                  </div>
                  <h3 className="font-display text-3xl tracking-wide chrome-text mb-3">MESSAGE SENT</h3>
                  <p className="text-sm" style={{ color: '#8ecfff' }}>
                    Thanks for reaching out. We'll reply to your email within a few hours.
                    For faster help, join our Discord server.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h2 className="font-display text-2xl tracking-wide chrome-text mb-6">SEND A MESSAGE</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-medium mb-2" style={{ color: '#8ecfff' }}>Name</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        className="bv-input w-full px-4 py-3 rounded-xl text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-2" style={{ color: '#8ecfff' }}>Email</label>
                      <input
                        type="text"
                        inputMode="email"
                        name="email"
                        autoComplete="email"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="your@email.com"
                        className="bv-input w-full px-4 py-3 rounded-xl text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: '#8ecfff' }}>Subject</label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      required
                      className="bv-input w-full px-4 py-3 rounded-xl text-sm cursor-pointer"
                    >
                      <option value="">Select a topic</option>
                      <option value="order">Order Issue / Delivery</option>
                      <option value="replacement">Replacement Request</option>
                      <option value="payment">Payment Question</option>
                      <option value="product">Product Question</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: '#8ecfff' }}>Message</label>
                    <textarea
                      required
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Describe your issue or question in detail..."
                      rows={5}
                      className="bv-input w-full px-4 py-3 rounded-xl text-sm resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="btn-primary w-full py-4 rounded-xl text-sm font-semibold disabled:opacity-60"
                  >
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
